import React, { useState, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  IconButton, 
  Tooltip, 
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  LinearProgress
} from '@mui/material';
import { 
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  ViewAgenda as ViewAgendaIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Shuffle as ShuffleIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';
import TimeSavedIndicator from '../TimeSavedIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FlashCard } from '../../services/contentService';

interface FlashCardsProps {
  flashCards: FlashCard[];
  timeSaved: number;
  videoTitle: string;
  isSaved?: boolean;
  onSave?: () => void;
  onCopy?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

const FlashCards: React.FC<FlashCardsProps> = ({
  flashCards,
  timeSaved,
  videoTitle,
  isSaved = false,
  onSave = () => {},
  onCopy = () => {},
  onShare = () => {},
  onDownload = () => {},
  onPrint = () => {}
}) => {
  // State for more options menu
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  
  // State for current card
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  
  // State for card flipped
  const [isFlipped, setIsFlipped] = useState(false);
  
  // State for mastered cards
  const [masteredCards, setMasteredCards] = useState<number[]>([]);
  
  // State for shuffled cards
  const [shuffledIndices, setShuffledIndices] = useState<number[]>(
    Array.from({ length: flashCards.length }, (_, i) => i)
  );
  
  // State for study mode
  const [studyMode, setStudyMode] = useState<'all' | 'remaining'>('all');
  
  // Ref for card element
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Handle more menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  
  // Handle more menu close
  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };
  
  // Handle copy
  const handleCopy = () => {
    const content = flashCards.map(card => 
      `Q: ${card.front}\nA: ${card.back}\n\n`
    ).join('');
    
    navigator.clipboard.writeText(content);
    onCopy();
  };
  
  // Handle save
  const handleSave = () => {
    onSave();
  };
  
  // Handle share
  const handleShare = () => {
    onShare();
    handleMoreMenuClose();
  };
  
  // Handle download
  const handleDownload = () => {
    // Create content string
    const content = flashCards.map(card => 
      `# ${card.front}\n\n${card.back}\n\n---\n\n`
    ).join('');
    
    // Create a blob with the content
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and click it
    const a = document.createElement('a');
    a.href = url;
    a.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_flashcards.txt`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    onDownload();
    handleMoreMenuClose();
  };
  
  // Handle print
  const handlePrint = () => {
    // Create content HTML
    const contentHtml = flashCards.map(card => 
      `<div class="flashcard">
        <div class="front">
          <h2>Front:</h2>
          <div class="content">${card.front}</div>
        </div>
        <div class="back">
          <h2>Back:</h2>
          <div class="content">${card.back.replace(/\n/g, '<br>')}</div>
        </div>
      </div>`
    ).join('');
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${videoTitle} - Flash Cards</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 20px;
                color: #333;
              }
              h1 {
                font-size: 24px;
                margin-bottom: 20px;
              }
              .time-saved {
                color: #06d6a0;
                font-weight: bold;
                margin-bottom: 20px;
              }
              .flashcard {
                margin-bottom: 30px;
                page-break-inside: avoid;
                border: 1px solid #ddd;
                border-radius: 8px;
                overflow: hidden;
              }
              .front, .back {
                padding: 15px;
              }
              .front {
                background-color: #f8f9fa;
                border-bottom: 1px solid #ddd;
              }
              h2 {
                font-size: 18px;
                margin-top: 0;
                margin-bottom: 10px;
                color: #00b4d8;
              }
              .content {
                margin-left: 15px;
              }
              @media print {
                body {
                  font-size: 12pt;
                }
                h2 {
                  font-size: 14pt;
                }
                .flashcard {
                  page-break-inside: avoid;
                  border: 1px solid #999;
                }
              }
            </style>
          </head>
          <body>
            <h1>${videoTitle} - Flash Cards</h1>
            <div class="time-saved">Saved ${timeSaved} minutes</div>
            ${contentHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
    
    onPrint();
    handleMoreMenuClose();
  };
  
  // Handle card flip
  const handleCardFlip = () => {
    setIsFlipped(!isFlipped);
  };
  
  // Handle next card
  const handleNextCard = () => {
    setIsFlipped(false);
    
    // Get the next card index based on study mode
    if (studyMode === 'remaining') {
      const remainingIndices = shuffledIndices.filter(index => !masteredCards.includes(index));
      if (remainingIndices.length > 1) {
        const currentIndexPosition = remainingIndices.indexOf(shuffledIndices[currentCardIndex]);
        const nextPosition = (currentIndexPosition + 1) % remainingIndices.length;
        const nextCardIndex = shuffledIndices.indexOf(remainingIndices[nextPosition]);
        setCurrentCardIndex(nextCardIndex);
      }
    } else {
      setCurrentCardIndex((currentCardIndex + 1) % shuffledIndices.length);
    }
  };
  
  // Handle previous card
  const handlePrevCard = () => {
    setIsFlipped(false);
    
    // Get the previous card index based on study mode
    if (studyMode === 'remaining') {
      const remainingIndices = shuffledIndices.filter(index => !masteredCards.includes(index));
      if (remainingIndices.length > 1) {
        const currentIndexPosition = remainingIndices.indexOf(shuffledIndices[currentCardIndex]);
        const prevPosition = (currentIndexPosition - 1 + remainingIndices.length) % remainingIndices.length;
        const prevCardIndex = shuffledIndices.indexOf(remainingIndices[prevPosition]);
        setCurrentCardIndex(prevCardIndex);
      }
    } else {
      setCurrentCardIndex((currentCardIndex - 1 + shuffledIndices.length) % shuffledIndices.length);
    }
  };
  
  // Handle mark as mastered
  const handleMarkAsMastered = () => {
    const cardIndex = shuffledIndices[currentCardIndex];
    if (!masteredCards.includes(cardIndex)) {
      setMasteredCards([...masteredCards, cardIndex]);
    }
    
    // Move to next card if there are remaining cards
    if (studyMode === 'remaining') {
      const remainingIndices = shuffledIndices.filter(
        index => !masteredCards.includes(index) && index !== cardIndex
      );
      
      if (remainingIndices.length > 0) {
        const nextCardIndex = shuffledIndices.indexOf(remainingIndices[0]);
        setCurrentCardIndex(nextCardIndex);
      }
    } else {
      handleNextCard();
    }
    
    setIsFlipped(false);
  };
  
  // Handle shuffle cards
  const handleShuffleCards = () => {
    // Fisher-Yates shuffle algorithm
    const indices = Array.from({ length: flashCards.length }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    
    setShuffledIndices(indices);
    setCurrentCardIndex(0);
    setIsFlipped(false);
  };
  
  // Handle reset mastered
  const handleResetMastered = () => {
    setMasteredCards([]);
  };
  
  // Handle study mode change
  const handleStudyModeChange = (mode: 'all' | 'remaining') => {
    setStudyMode(mode);
    
    // If switching to 'remaining' mode and current card is mastered,
    // find the first non-mastered card
    if (mode === 'remaining') {
      const currentCardActualIndex = shuffledIndices[currentCardIndex];
      if (masteredCards.includes(currentCardActualIndex)) {
        const remainingIndices = shuffledIndices.filter(index => !masteredCards.includes(index));
        if (remainingIndices.length > 0) {
          const nextCardIndex = shuffledIndices.indexOf(remainingIndices[0]);
          setCurrentCardIndex(nextCardIndex);
        }
      }
    }
  };
  
  // Get current card
  const currentCardActualIndex = shuffledIndices[currentCardIndex];
  const currentCard = flashCards[currentCardActualIndex];
  
  // Calculate progress
  const progress = (masteredCards.length / flashCards.length) * 100;
  
  // Get remaining cards count
  const remainingCardsCount = flashCards.length - masteredCards.length;
  
  // Check if current card is mastered
  const isCurrentCardMastered = masteredCards.includes(currentCardActualIndex);
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ViewAgendaIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            Flash Cards
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimeSavedIndicator minutes={timeSaved} />
          
          <Box sx={{ display: 'flex', ml: 2 }}>
            <Tooltip title={isSaved ? "Saved to your library" : "Save to your library"}>
              <IconButton onClick={handleSave} color={isSaved ? "primary" : "default"}>
                {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More options">
              <IconButton
                onClick={handleMoreMenuOpen}
                aria-label="more options"
                aria-controls="more-menu"
                aria-haspopup="true"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Review key concepts with these flash cards.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Shuffle cards">
            <IconButton onClick={handleShuffleCards} size="small">
              <ShuffleIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Reset mastered cards">
            <IconButton onClick={handleResetMastered} size="small">
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: 'rgba(0, 180, 216, 0.1)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(45deg, #06d6a0 30%, #06d6a0 90%)',
              borderRadius: 4,
            }
          }} 
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {masteredCards.length} of {flashCards.length} mastered
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            {remainingCardsCount} remaining
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Stack direction="row" spacing={1}>
          <Chip
            label="All Cards"
            color={studyMode === 'all' ? 'primary' : 'default'}
            onClick={() => handleStudyModeChange('all')}
            variant={studyMode === 'all' ? 'filled' : 'outlined'}
            sx={{ borderRadius: 4 }}
          />
          <Chip
            label="Remaining Cards"
            color={studyMode === 'remaining' ? 'primary' : 'default'}
            onClick={() => handleStudyModeChange('remaining')}
            variant={studyMode === 'remaining' ? 'filled' : 'outlined'}
            sx={{ borderRadius: 4 }}
            disabled={remainingCardsCount === 0}
          />
        </Stack>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Flash Card */}
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box
          ref={cardRef}
          onClick={handleCardFlip}
          sx={{
            width: '100%',
            maxWidth: 500,
            height: 300,
            perspective: '1000px',
            cursor: 'pointer',
            mb: 2
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            {/* Front of card */}
            <Card
              elevation={0}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 4,
                bgcolor: 'rgba(0, 180, 216, 0.05)',
                ...(isCurrentCardMastered && {
                  border: '1px solid rgba(6, 214, 160, 0.3)',
                  bgcolor: 'rgba(6, 214, 160, 0.05)',
                }),
              }}
            >
              <CardContent sx={{ width: '100%', textAlign: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {currentCard.front}
                </Typography>
                
                <Box sx={{ mt: 3, opacity: 0.7 }}>
                  <Typography variant="caption" color="text.secondary">
                    Click to flip
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            
            {/* Back of card */}
            <Card
              elevation={0}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 3,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 4,
                bgcolor: 'rgba(255, 158, 0, 0.05)',
                ...(isCurrentCardMastered && {
                  border: '1px solid rgba(6, 214, 160, 0.3)',
                  bgcolor: 'rgba(6, 214, 160, 0.05)',
                }),
                overflowY: 'auto'
              }}
            >
              <CardContent sx={{ width: '100%' }}>
                <Box 
                  sx={{ 
                    typography: 'body1',
                    '& p': {
                      mb: 1,
                      '&:last-child': {
                        mb: 0
                      }
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {currentCard.back}
                  </ReactMarkdown>
                </Box>
                
                <Box sx={{ mt: 3, opacity: 0.7, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Click to flip back
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handlePrevCard}
            sx={{ borderRadius: 4 }}
          >
            Previous
          </Button>
          
          <Typography variant="body2" color="text.secondary">
            {currentCardIndex + 1} / {flashCards.length}
          </Typography>
          
          <Button
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNextCard}
            sx={{ borderRadius: 4 }}
          >
            Next
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleMarkAsMastered}
          disabled={isCurrentCardMastered}
          sx={{ 
            borderRadius: 4,
            px: 3
          }}
        >
          {isCurrentCardMastered ? 'Already Mastered' : 'Mark as Mastered'}
        </Button>
      </Box>
      
      {/* More options menu */}
      <Menu
        id="more-menu"
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMoreMenuClose}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default FlashCards;
